// ไฟล์: frontend/src/pages/InnovationDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const InnovationDetailPage = ({ user }) => {
  const { id } = useParams();
  const [innovation, setInnovation] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [userRating, setUserRating] = useState(0);
  const [inLibrary, setInLibrary] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [similarInnovations, setSimilarInnovations] = useState([]);

  useEffect(() => {
    const fetchInnovationData = async () => {
      try {
        // ดึงข้อมูลนวัตกรรม
        const response = await axios.get(`/innovations/${id}`);
        setInnovation(response.data.innovation);
        setComments(response.data.comments);
        
        // ดึงข้อมูลเพิ่มเติมถ้ามีการล็อกอิน
        if (user) {
          // ดึงคะแนนของผู้ใช้
          const ratingResponse = await axios.get(`/ratings/user?innovation_id=${id}`);
          setUserRating(ratingResponse.data.rating || 0);
          
          // ตรวจสอบว่าอยู่ในคลังหรือไม่
          const libraryResponse = await axios.get('/library');
          const isInLibrary = libraryResponse.data.items.some(
            item => item.innovation_id === parseInt(id)
          );
          setInLibrary(isInLibrary);
          
          // ตรวจสอบสถานะการติดตามผู้สร้าง
          if (response.data.innovation.user_id) {
            try {
              const followResponse = await axios.get(`/follow/status/${response.data.innovation.user_id}`);
              setFollowed(followResponse.data.followed);
            } catch (error) {
              console.error('Error checking follow status:', error);
            }
          }
        }
        
        // ดึงนวัตกรรมที่คล้ายกัน
        const similarResponse = await axios.get('/innovations', {
          params: {
            category_id: response.data.innovation.category_id,
            limit: 3,
            exclude_id: id
          }
        });
        setSimilarInnovations(similarResponse.data.innovations.slice(0, 3));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching innovation data:', error);
        setLoading(false);
      }
    };
    
    fetchInnovationData();
    // ย้อนกลับไปด้านบนของหน้า
    window.scrollTo(0, 0);
  }, [id, user]);

  // ให้คะแนนนวัตกรรม
  const handleRate = async (rating) => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนให้คะแนน',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }

    try {
      await axios.post('/ratings', {
        rating,
        innovation_id: id
      });
      
      setUserRating(rating);
      
      // อัพเดทคะแนนเฉลี่ยในข้อมูลนวัตกรรม
      setInnovation(prev => ({
        ...prev,
        avg_rating: prev.rating_count > 0 
          ? ((prev.avg_rating * prev.rating_count) + rating) / (prev.rating_count + 1)
          : rating,
        rating_count: prev.rating_count + 1
      }));
      
      Swal.fire({
        title: 'ให้คะแนนสำเร็จ',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error rating innovation:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถให้คะแนนได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // เพิ่มความคิดเห็น
  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนแสดงความคิดเห็น',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }
    
    if (!commentText.trim()) {
      Swal.fire({
        title: 'ข้อความว่างเปล่า',
        text: 'กรุณาป้อนข้อความก่อนส่ง',
        icon: 'warning'
      });
      return;
    }
    
    try {
      const response = await axios.post('/comments', {
        content: commentText,
        innovation_id: id
      });
      
      setComments([response.data.comment, ...comments]);
      setCommentText('');
      
      Swal.fire({
        title: 'ความคิดเห็นถูกเพิ่มแล้ว',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถเพิ่มความคิดเห็นได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // ลบความคิดเห็น
  const handleDeleteComment = async (commentId) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณต้องการลบความคิดเห็นนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/comments/${commentId}`);
          setComments(comments.filter(comment => comment.id !== commentId));
          
          Swal.fire({
            title: 'ลบความคิดเห็นสำเร็จ',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Error deleting comment:', error);
          Swal.fire({
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบความคิดเห็นได้ในขณะนี้',
            icon: 'error'
          });
        }
      }
    });
  };

  // บันทึกในคลัง
  const handleToggleLibrary = async () => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนบันทึกในคลัง',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }
    
    try {
      if (inLibrary) {
        // ค้นหา ID ของรายการในคลัง
        const libraryResponse = await axios.get('/library');
        const libraryItem = libraryResponse.data.items.find(
          item => item.innovation_id === parseInt(id)
        );
        
        if (libraryItem) {
          await axios.delete(`/library/${libraryItem.library_id}`);
          setInLibrary(false);
          
          Swal.fire({
            title: 'นำออกจากคลังสำเร็จ',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        }
      } else {
        await axios.post('/library', {
          innovation_id: id,
          status: 'saved'
        });
        
        setInLibrary(true);
        
        Swal.fire({
          title: 'บันทึกในคลังสำเร็จ',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error toggling library:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถดำเนินการได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // ติดตาม/เลิกติดตามผู้สร้าง
  const handleToggleFollow = async () => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนติดตาม',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }
    
    try {
      const response = await axios.post('/follow', {
        teacherId: innovation.user_id
      });
      
      setFollowed(response.data.followed);
      
      Swal.fire({
        title: response.data.followed ? 'ติดตามสำเร็จ' : 'เลิกติดตามสำเร็จ',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error toggling follow:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถดำเนินการได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  // ถ้าไม่พบนวัตกรรม
  if (!innovation) {
    return (
      <div className="container">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i> ไม่พบนวัตกรรมที่คุณต้องการ
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="detail-header">
        <div className="detail-info">
          <h1 className="detail-title">{innovation.title}</h1>
          
          <div className="detail-meta">
            <div>
              <i className="fas fa-folder"></i> {innovation.category_name}
            </div>
            <div>
              <i className="fas fa-book"></i> {innovation.subject_name}
            </div>
            <div>
              <i className="fas fa-graduation-cap"></i> {innovation.grade_level_name}
            </div>
            <div>
              <i className="fas fa-eye"></i> {innovation.view_count} ครั้ง
            </div>
            <div>
              <i className="fas fa-calendar-alt"></i> {new Date(innovation.created_at).toLocaleDateString('th-TH')}
            </div>
          </div>
          
          <div className="rating-container mb-4">
            <div className="rating-title">ให้คะแนนนวัตกรรมนี้</div>
            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map(star => (
                <i 
                  key={star}
                  className={`fas fa-star ${star <= userRating ? 'active' : ''}`}
                  onClick={() => handleRate(star)}
                ></i>
              ))}
              <span className="rating-count">
                ({innovation.rating_count} คะแนน | เฉลี่ย {innovation.avg_rating ? innovation.avg_rating.toFixed(1) : '0.0'})
              </span>
            </div>
          </div>
          
          <div className="detail-actions">
            {innovation.file_path && (
              <a 
                href={`${process.env.REACT_APP_API_URL}/uploads/${innovation.file_path}`} 
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-download"></i> ดาวน์โหลดไฟล์
              </a>
            )}
            
            <button 
              className={`btn ${inLibrary ? 'btn-danger' : 'btn-info'}`}
              onClick={handleToggleLibrary}
            >
              <i className={`fas ${inLibrary ? 'fa-trash' : 'fa-bookmark'}`}></i> 
              {inLibrary ? 'นำออกจากคลัง' : 'บันทึกในคลัง'}
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={() => {
                navigator.share({
                  title: innovation.title,
                  text: innovation.description,
                  url: window.location.href
                }).catch(err => {
                  console.error('Error sharing:', err);
                });
              }}
            >
              <i className="fas fa-share-alt"></i> แชร์
            </button>
          </div>
          
          <div className="detail-author">
            <img 
              src={innovation.author_image 
                ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${innovation.author_image}`
                : `https://ui-avatars.com/api/?name=${innovation.author_name}&background=random`} 
              alt={innovation.author_name} 
            />
            <div className="detail-author-info">
              <div className="detail-author-name">
                <Link to={`/teachers/${innovation.user_id}`}>{innovation.author_name}</Link>
              </div>
              <div className="detail-author-school">{innovation.school || 'ครูผู้สอน'}</div>
              
              {user && user.id !== innovation.user_id && (
                <button 
                  className={`btn btn-sm ${followed ? 'btn-light' : 'btn-outline'} mt-2`}
                  onClick={handleToggleFollow}
                >
                  <i className={`fas ${followed ? 'fa-user-minus' : 'fa-user-plus'}`}></i> 
                  {followed ? 'เลิกติดตาม' : 'ติดตาม'}
                </button>
              )}
            </div>
          </div>
        </div>
        
        <img 
          src={innovation.thumbnail 
            ? `${process.env.REACT_APP_API_URL}/uploads/${innovation.thumbnail}`
            : '/assets/default-innovation.jpg'} 
          alt={innovation.title} 
          className="detail-image"
        />
      </div>
      
      <div className="detail-sections">
        <div className="detail-content">
          <h2 className="content-title">
            <i className="fas fa-info-circle"></i> รายละเอียด
          </h2>
          
          <div className="content-section">
            <h3>คำอธิบาย</h3>
            <p className="detail-description">{innovation.description}</p>
          </div>
          
          <div className="content-section">
            <h3>เนื้อหา</h3>
            <div className="innovation-content">
              {innovation.content ? (
                <div dangerouslySetInnerHTML={{ __html: innovation.content.replace(/\n/g, '<br>') }}></div>
              ) : (
                <p>ไม่มีเนื้อหา</p>
              )}
            </div>
          </div>
          
          <div className="comments-section">
            <h2 className="comments-title">
              <i className="fas fa-comments"></i> ความคิดเห็น ({comments.length})
            </h2>
            
            <div className="comment-form">
              <form onSubmit={handleAddComment}>
                <div className="form-group">
                  <textarea 
                    className="form-control"
                    placeholder="แสดงความคิดเห็นของคุณ..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows="3"
                  ></textarea>
                </div>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-paper-plane"></i> ส่งความคิดเห็น
                </button>
              </form>
            </div>
            
            <div className="comment-list">
              {comments.length > 0 ? (
                comments.map(comment => (
                  <div className="comment" key={comment.id}>
                    <img 
                      src={comment.profile_image 
                        ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${comment.profile_image}`
                        : `https://ui-avatars.com/api/?name=${comment.fullname}&background=random`} 
                      alt={comment.fullname} 
                      className="comment-avatar"
                    />
                    <div className="comment-content">
                      <div className="comment-header">
                        <div className="comment-author">{comment.fullname}</div>
                        <div className="comment-date">
                          {new Date(comment.created_at).toLocaleDateString('th-TH')}
                        </div>
                      </div>
                      <div className="comment-text">{comment.content}</div>
                      <div className="comment-actions">
                        {user && (user.id === comment.user_id || user.role === 'admin') && (
                          <div 
                            className="comment-action"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            <i className="fas fa-trash"></i> ลบ
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-comments">ยังไม่มีความคิดเห็น เป็นคนแรกที่แสดงความคิดเห็นสิ!</div>
              )}
            </div>
          </div>
        </div>
        
        <div className="detail-sidebar">
          <div className="sidebar-card">
            <div className="sidebar-card-title">
              <i className="fas fa-lightbulb"></i> นวัตกรรมที่คล้ายกัน
            </div>
            <div className="similar-resources">
              {similarInnovations.length > 0 ? (
                similarInnovations.map(item => (
                  <div className="similar-resource" key={item.id}>
                    <img 
                      src={item.thumbnail 
                        ? `${process.env.REACT_APP_API_URL}/uploads/${item.thumbnail}`
                        : '/assets/default-innovation.jpg'} 
                      alt={item.title} 
                      className="similar-resource-image"
                    />
                    <div className="similar-resource-info">
                      <Link to={`/innovations/${item.id}`} className="similar-resource-title">
                        {item.title}
                      </Link>
                      <div className="similar-resource-meta">
                        <div className="similar-resource-author">
                          โดย {item.author_name}
                        </div>
                        <div className="similar-resource-rating">
                          <i className="fas fa-star"></i> {item.avg_rating ? item.avg_rating.toFixed(1) : '0.0'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-similar">ไม่พบนวัตกรรมที่คล้ายกัน</div>
              )}
            </div>
          </div>
          
          <div className="sidebar-card">
            <div className="sidebar-card-title">
              <i className="fas fa-tag"></i> แท็ก
            </div>
            <div className="tags-container">
              <div className="tag-item">{innovation.category_name}</div>
              <div className="tag-item">{innovation.subject_name}</div>
              <div className="tag-item">{innovation.grade_level_name}</div>
              <div className="tag-item">นวัตกรรม</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InnovationDetailPage;